import React from 'react';
import { useNavigate } from 'react-router-dom';
import { Sparkles, Crown } from 'lucide-react';

/**
 * Popup exibido quando o usuário não tem créditos suficientes para gerar.
 * Usado pelos dois fluxos de criação (manual e Instagram) para que a
 * mensagem seja idêntica em qualquer caminho.
 *
 * Props:
 *  - open: boolean
 *  - balance: número de créditos disponíveis (null = desconhecido)
 *  - required: créditos necessários para a criação atual (opcional)
 *  - message: texto customizado (opcional; senão monta automático)
 *  - onClose: fecha o popup
 */
const CreditsExhaustedModal = ({ open, balance = null, required = null, message = null, onClose }) => {
  const navigate = useNavigate();

  if (!open) return null;

  const isEmpty = balance === 0;
  const title = isEmpty ? 'Seus créditos acabaram' : 'Créditos insuficientes';
  const body = message || (
    isEmpty
      ? 'Escolha um plano para continuar criando seus conteúdos.'
      : (balance !== null && required !== null
          ? `Você tem ${balance} crédito(s) e esta criação precisa de ${required}.`
          : 'Você não tem créditos suficientes para esta criação.')
  );

  return (
    <div
      className="fixed inset-0 bg-black/60 z-50 flex items-center justify-center p-4 animate-fadeIn"
      onClick={onClose}
    >
      <div
        className="bg-white rounded-3xl shadow-2xl max-w-md w-full overflow-hidden"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className="p-6 bg-gradient-to-r from-amber-50 to-orange-50 border-b border-amber-100 text-center">
          <div className="w-16 h-16 mx-auto mb-4 bg-gradient-to-br from-amber-100 to-orange-100 rounded-2xl flex items-center justify-center">
            <Crown className="w-8 h-8 text-amber-600" />
          </div>
          <h3 className="text-xl font-bold text-gray-900">{title}</h3>
        </div>

        {/* Body */}
        <div className="p-6">
          <p className="text-gray-600 text-center mb-6">{body}</p>

          <div className="p-4 bg-amber-50 border border-amber-200 rounded-2xl mb-6">
            <p className="text-sm text-amber-800 text-center">
              <Sparkles className="w-4 h-4 inline-block mr-1" />
              Com um plano você recebe créditos todo mês: 1 crédito por imagem e 5 por vídeo.
            </p>
          </div>

          <div className="space-y-3">
            <button
              type="button"
              onClick={() => navigate('/plans')}
              className="w-full py-3 px-6 bg-gradient-to-r from-amber-400 to-orange-500 hover:from-amber-500 hover:to-orange-600 text-white font-semibold rounded-xl transition-all shadow-lg"
            >
              Ver planos e assinar
            </button>
            <button
              type="button"
              onClick={onClose}
              className="w-full py-3 px-6 text-gray-500 hover:text-gray-700 hover:bg-gray-100 rounded-xl transition-all"
            >
              Voltar
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default CreditsExhaustedModal;
